import { List, ListItem, ListItemText, Typography } from '@mui/material';
import type { Outage, ServiceStatus } from './types.js';
import { formatTimeOfDay, relativeTime } from './format.js';

interface Props {
  outages: Outage[];
  services: ServiceStatus[];
}

function displayFor(services: ServiceStatus[], serviceId: string): string {
  return services.find((s) => s.service === serviceId)?.display ?? serviceId;
}

function describe(outage: Outage, services: ServiceStatus[]): { primary: string; secondary: string } {
  const name = displayFor(services, outage.service);
  if (outage.ended_at && outage.duration_minutes !== null) {
    const min = outage.duration_minutes;
    return {
      primary: `${name} — down ${min} min`,
      secondary: `Ended ${formatTimeOfDay(outage.ended_at)} · ${relativeTime(outage.ended_at)}`,
    };
  }
  return {
    primary: `${name} — ongoing`,
    secondary: `Started ${formatTimeOfDay(outage.started_at)} · ${relativeTime(outage.started_at)}`,
  };
}

/**
 * Render outages newest-first with an empty state. The 7-day window is
 * enforced on the server side; the empty-state copy below mirrors the spec.
 */
export function OutagesList({ outages, services }: Props) {
  if (outages.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No outages in the last 7 days.
      </Typography>
    );
  }

  const sorted = [...outages].sort((a, z) =>
    a.started_at < z.started_at ? 1 : a.started_at > z.started_at ? -1 : 0,
  );

  return (
    <List disablePadding>
      {sorted.map((o, idx) => {
        const { primary, secondary } = describe(o, services);
        return (
          <ListItem
            key={`${o.service}-${o.check}-${o.started_at}-${idx}`}
            disableGutters
            divider={idx < sorted.length - 1}
          >
            <ListItemText
              primary={primary}
              secondary={secondary}
              slotProps={{
                primary: { variant: 'body2' },
                secondary: { variant: 'caption' },
              }}
            />
          </ListItem>
        );
      })}
    </List>
  );
}
